import React, { useState, useEffect, useMemo } from 'react';
import { Activity, Calendar, Users, Search, Plus, Edit, Trash2, Loader2, FileText, X } from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getBatches } from '../../firebase/collections';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, updateDoc, deleteDoc, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

const ITEMS_PER_PAGE = 15;

const ActivitiesManagementOptimized = () => {
  const { userProfile } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('forms');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activityForms, setActivityForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', category: 'sports', startDate: new Date().toISOString().split('T')[0],
    endDate: '', fields: [{ label: '', type: 'text', required: false }], isActive: true
  });

  const isSuperAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin' || userProfile?.role === 'founder';
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

  useEffect(() => {
    loadBatches();
  }, [coachingId]);

  useEffect(() => {
    if (selectedBatch) {
      loadAllData();
    }
  }, [selectedBatch]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const result = await getBatches(coachingId || null);
      if (result.success) setBatches(result.data || []);
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    if (!selectedBatch) return;
    try {
      setLoading(true);
      await Promise.all([loadActivityForms(), loadSubmissions()]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadActivityForms = async () => {
    if (!selectedBatch) return;
    try {
      const q = query(collection(db, 'activityForms'), where('batchId', '==', selectedBatch.id), limit(100));
      const snapshot = await getDocs(q);
      const forms = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      forms.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
      setActivityForms(forms);
    } catch (error) {
      console.error('Error loading forms:', error);
    }
  };

  const loadSubmissions = async () => {
    if (!selectedBatch) return;
    try {
      const q = query(collection(db, 'activityFormSubmissions'), where('batchId', '==', selectedBatch.id), limit(100));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
      setSubmissions(data);
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const handleAddField = () => {
    setFormData({
      ...formData,
      fields: [...formData.fields, { label: '', type: 'text', required: false }]
    });
  };

  const handleRemoveField = (index) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: newFields });
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], [field]: value };
    setFormData({ ...formData, fields: newFields });
  };

  const handleSaveForm = async () => {
    if (!selectedBatch || !formData.title || formData.fields.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const formDataToSave = {
        batchId: selectedBatch.id, title: formData.title, description: formData.description || '',
        category: formData.category, startDate: formData.startDate, endDate: formData.endDate || null,
        fields: formData.fields.filter(f => f.label.trim() !== ''), isActive: formData.isActive,
        createdBy: userProfile?.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
      };

      if (editingForm) {
        await updateDoc(doc(db, 'activityForms', editingForm.id), { ...formDataToSave, updatedAt: serverTimestamp() });
        toast.success('Form updated successfully');
      } else {
        await addDoc(collection(db, 'activityForms'), formDataToSave);
        toast.success('Form created successfully');
      }

      setShowFormModal(false);
      setEditingForm(null);
      setFormData({ title: '', description: '', category: 'sports', startDate: new Date().toISOString().split('T')[0],
        endDate: '', fields: [{ label: '', type: 'text', required: false }], isActive: true });
      loadActivityForms();
    } catch (error) {
      toast.error('Failed to save form');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await deleteDoc(doc(db, 'activityForms', formId));
      toast.success('Form deleted');
      loadActivityForms();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  // Memoized pagination
  const { paginatedForms, formsTotalPages } = useMemo(() => {
    const filtered = activityForms.filter(f => f.title?.toLowerCase().includes(searchTerm.toLowerCase()));
    const total = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return { paginatedForms: filtered.slice(start, start + ITEMS_PER_PAGE), formsTotalPages: total };
  }, [activityForms, searchTerm, currentPage]);

  const { paginatedSubmissions, submissionsTotalPages } = useMemo(() => {
    const filtered = submissions.filter(s => s.studentName?.toLowerCase().includes(searchTerm.toLowerCase()));
    const total = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return { paginatedSubmissions: filtered.slice(start, start + ITEMS_PER_PAGE), submissionsTotalPages: total };
  }, [submissions, searchTerm, currentPage]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
          <Activity className="w-8 h-8 mr-3 text-purple-600" /> Activities Management (Optimized)
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Manage co-curricular activity forms and submissions</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Batch</label>
        <select
          value={selectedBatch?.id || ''}
          onChange={(e) => { setSelectedBatch(batches.find(b => b.id === e.target.value)); setCurrentPage(1); }}
          className="w-full md:w-1/3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a batch</option>
          {batches.map((batch) => <option key={batch.id} value={batch.id}>{batch.name}</option>)}
        </select>
      </div>

      {selectedBatch && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px">
              {['forms', 'submissions'].map(tab => (
                <button key={tab} onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                  className={`py-4 px-6 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500'
                  }`}>
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'forms' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Activity Forms</h2>
                  <button onClick={() => { setEditingForm(null); setShowFormModal(true); }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center space-x-2">
                    <Plus className="w-4 h-4" /><span>Create Form</span>
                  </button>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input type="text" placeholder="Search forms..." value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginatedForms.map((form) => (
                    <div key={form.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold">{form.title}</h3>
                          <p className="text-sm text-gray-600 capitalize">{form.category}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button onClick={() => { setEditingForm(form); setFormData(form); setShowFormModal(true); }}
                            className="text-blue-600"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteForm(form.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${form.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        {form.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>

                {formsTotalPages > 1 && (
                  <div className="flex justify-center space-x-2 mt-4">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
                    <span className="text-sm">Page {currentPage} of {formsTotalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(formsTotalPages, p + 1))}
                      disabled={currentPage === formsTotalPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'submissions' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Student Submissions</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input type="text" placeholder="Search submissions..." value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white" />
                </div>
                <p className="text-gray-600">Total submissions: {submissions.length}</p>
                {submissionsTotalPages > 1 && (
                  <div className="flex justify-center space-x-2 mt-4">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
                    <span className="text-sm">Page {currentPage} of {submissionsTotalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(submissionsTotalPages, p + 1))}
                      disabled={currentPage === submissionsTotalPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingForm ? 'Edit Form' : 'Create Activity Form'}
              </h3>
              <button onClick={() => setShowFormModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Form Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="sports">Sports</option>
                    <option value="cultural">Cultural</option>
                    <option value="academic">Academic</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Form Fields *
                  </label>
                  <button
                    onClick={handleAddField}
                    className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
                  >
                    Add Field
                  </button>
                </div>
                {formData.fields.map((field, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      placeholder="Field label"
                      value={field.label}
                      onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                    <select
                      value={field.type}
                      onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="date">Date</option>
                    </select>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                        className="mr-1"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
                    </label>
                    <button
                      onClick={() => handleRemoveField(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveForm}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitiesManagementOptimized;
